<template>
  <div>
    <div class="tw-pt-6">
      <WdsStack justify-space-between>
        <WdsStack gap-sm>
          <DialogShortlistingButton
            v-if="!isClosed"
            :dialog-local-id="dialog.localId"
            :account-global-id="accountGlobalId"
            :yjob-local-id="yjob.localId"
            :shortlist-local-id="shortlistId"
            @added-to-shortlist="$emit('added-to-shortlist')"
            @removed-from-shortlist="$emit('removed-from-shortlist')"
          ></DialogShortlistingButton>
          <WdsButton v-if="isClosed" :disabled="isResumingDialog" @click="resumeDialog">
            {{ $t('Resume conversation') }}
            <transition name="fade">
              <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
              <button-spinner v-if="isResumingDialog" />
            </transition>
          </WdsButton>
          <WdsButton v-else :title="$t('End conversation with a message')" @click="openModal">
            {{ $t('End conversation') }}
          </WdsButton>
        </WdsStack>
        <a :href="dialog.url" data-test="view-conversion-button">
          <WdsButton primary>
            <WdsStack align-center>
              {{ $t('View') }}
              <i class="tw-ml-2 zmdi zmdi-arrow-right" />
            </WdsStack>
          </WdsButton>
        </a>
      </WdsStack>
    </div>

    <ModalCloseConversation
      v-if="!dialog.hire"
      :dialog-global-id="dialog.globalId"
      :open="modalOpen"
      :default-close-message="defaultCloseMessage"
      @success="$emit('dialog-closed')"
      @modal-closed="onConversationModalClose"
    />
    <ModalCloseJob
      v-else-if="dialog.freelancer"
      :id="dialog.localId"
      :freelancer-name="dialog.freelancer.name"
      :open="modalOpen"
      :default-close-message="defaultCloseMessage"
      @success="$emit('dialog-closed')"
      @modal-closed="onConversationModalClose"
    />
  </div>
</template>

<script>
import gql from 'graphql-tag'
import { WdsStack, WdsButton } from '@worksome/wds-components'
import { graphqlFragmentValidator } from '@/utils/propValidation'
import ModalCloseConversation from '@/components/ModalCloseConversation.vue'
import DialogShortlistingButton from '@/components/DialogShortlistingButton.vue'
import { BASE_FRAGMENT_FOR_DIALOG_MUTATIONS } from '@/state/apollo/fragments'
import { NetworkError, Message } from '@/utils/notification'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import ModalCloseJob from '@/components/ModalCloseJob.vue'

const JOB_PAGE_DIALOGS_ITEM_FRAGMENT_FOR_DIALOG = gql`
  fragment JobPageDialogsItemFooterFragmentForDialog on Dialog {
    globalId
    url
    localId
    irrelevant
    dialogType
    shortlisted(yjobId: $yjobId) {
      data {
        localId
      }
    }
    freelancer {
      globalId
    }
    recruiter {
      globalId
    }
  }
`

const JOB_PAGE_DIALOGS_ITEM_FRAGMENT_FOR_YJOB = gql`
  fragment JobPageDialogsItemFooterFragmentForYjob on Yjob {
    localId
    company {
      settings {
        close_dialog_default_message
      }
    }
  }
`

/**
 * @emits dialog-unflagged
 * @emits dialog-flagged
 * @emits dialog-reopened
 * @emits dialog-closed
 * @emits removed-from-shortlist
 * @emits added-to-shortlist
 */
export default {
  components: {
    ModalCloseJob,
    ModalCloseConversation,
    DialogShortlistingButton,
    WdsStack,
    WdsButton,
  },
  props: {
    dialog: {
      type: Object,
      required: true,
      validator: graphqlFragmentValidator(JOB_PAGE_DIALOGS_ITEM_FRAGMENT_FOR_DIALOG),
    },
    yjob: {
      type: Object,
      required: true,
      validator: graphqlFragmentValidator(JOB_PAGE_DIALOGS_ITEM_FRAGMENT_FOR_YJOB),
    },
  },
  fragments: {
    dialog: JOB_PAGE_DIALOGS_ITEM_FRAGMENT_FOR_DIALOG,
    yjob: JOB_PAGE_DIALOGS_ITEM_FRAGMENT_FOR_YJOB,
  },
  data: () => ({
    modalOpen: false,
    isResumingDialog: false,
  }),
  computed: {
    shortlistId() {
      return this.dialog?.shortlisted?.data?.[0]?.localId
    },
    isClosed() {
      return this.dialog.dialogType.toLowerCase() === 'closed'
    },
    defaultCloseMessage() {
      return this.yjob.company?.settings?.close_dialog_default_message
    },
    accountGlobalId() {
      return this.dialog.freelancer?.globalId ?? this.dialog.recruiter?.globalId
    },
  },
  methods: {
    async resumeDialog() {
      this.isResumingDialog = true
      await this.$apollo
        .mutate({
          mutation: gql`
            mutation ReopenDialog($globalId: ID!) {
              reopenDialog(globalId: $globalId) {
                ...BaseFragmentForDialogMutations
                dialogType
                recruiter {
                  globalId
                  name
                }
                freelancer {
                  globalId
                  name
                }
              }
            }
            ${BASE_FRAGMENT_FOR_DIALOG_MUTATIONS}
          `,
          variables: {
            globalId: this.dialog.globalId,
          },
        })
        .then(({ data }) => {
          if (this.dialog.irrelevant) {
            this.$emit(`dialog-unflagged`)
          } else {
            this.$emit(`dialog-reopened`)
          }
          new Message(
            $t('Moved :name to new conversations', {
              name: data.reopenDialog?.recruiter?.name ?? data.reopenDialog?.freelancer?.name,
            }),
          ).dispatch(this)

          window.location.href = laravelNamedRoute(namedRoutes.messages.show, {
            id: this.dialog.localId,
          })
        })
        .catch((exception) => {
          new NetworkError({
            msg: this.$t('Error occurred. The action was not completed'),
            ex: exception,
          }).dispatch(this)
        })
        .finally(() => {
          this.isResumingDialog = false
        })
    },
    async flagDialogIrrelevant() {
      await this.$apollo
        .mutate({
          mutation: gql`
            mutation CloseDialog($globalId: ID!, $irrelevant: Boolean!) {
              closeDialog(globalId: $globalId, irrelevant: $irrelevant) {
                ...BaseFragmentForDialogMutations
                dialogType
                recruiter {
                  globalId
                  name
                }
              }
            }
            ${BASE_FRAGMENT_FOR_DIALOG_MUTATIONS}
          `,
          variables: {
            globalId: this.dialog.globalId,
            irrelevant: true,
          },
        })
        .then(({ data }) => {
          new Message(
            $t('Closed conversation with :name', {
              name: data.closeDialog?.recruiter?.name,
            }),
          ).dispatch(this)
          this.$emit(`dialog-flagged`)
        })
        .catch((exception) => {
          new NetworkError({
            msg: this.$t('Error occurred. The action was not completed'),
            ex: exception,
          }).dispatch(this)
        })
    },
    onConversationModalClose() {
      this.modalOpen = false
    },
    openModal() {
      this.modalOpen = true
    },
  },
}
</script>

<style lang="scss">
#vuePageDialogs {
  .profile-image {
    max-width: 50px;
  }

  .skills {
    margin-bottom: 0;
  }

  .d-flex {
    display: flex;
  }

  .justify-content-between {
    justify-content: space-between;
  }

  .flex-none {
    flex: none;
  }
}
</style>
